import { User, EducationLevel, PreferredStudyTime, LearningStyle, ChallengingTopic, WrongAnswer, TestResult } from '@/models/UserTypes';
import { AdaptiveMetrics } from '@/models/AdaptiveState';
import { AdaptiveMetrics as AdaptiveLearningMetrics } from '@/types/AdaptiveLearningTypes';
import questions from '@/utils/questions.json';
import { QuizType, QuizMode } from '@/models/SharedTypes';

// Safe enum access with fallbacks
const safeEducationLevel = EducationLevel || {
  MASTERS: "Master's in Social Work",
  BACHELORS: "Bachelor's in Social Work", 
  OTHER: "Other related degree"
};

const safePreferredStudyTime = PreferredStudyTime || {
  EVENING: "Evening",
  MORNING: "Morning",
  AFTERNOON: "Afternoon",
  NIGHT: "Night"
};

const safeChallengingTopic = ChallengingTopic || {
  DIAGNOSIS: "Diagnosis",
  TREATMENT_PLANNING: "Treatment Planning"
};

const safeLearningStyle = LearningStyle || {
  KINESTHETIC: "kinesthetic",
  VISUAL: "visual",
  AUDITORY: "auditory", 
  READING_WRITING: "reading_writing"
};

const safeQuizType = QuizType || {
  SAMPLE: "sample",
  FULL: "full",
  CATEGORY: "category"
};

// Convert JSON questions to match our Question type format
const formattedQuestions = questions.map(q => ({
  id: q.id.toString(),
  category: q.Category,
  question: q.Questions,
  correct_answer: q["CORRECT ANSWER"],
  option_a: q.A,
  option_b: q.B,
  option_c: q.C,
  option_d: q.D,
  explanation: q.Explanation,
  hint: q.HINT
}));

// Generate more realistic mock wrong answers
const generateMockWrongAnswers = (count: number): WrongAnswer[] => {
  const categories = ['ETHICS', 'DIAGNOSIS', 'TREATMENT_PLANNING'];
  const questions = [
    "What is the appropriate course of action when a client reveals suicidal ideation?",
    "Which diagnostic criteria are essential for Major Depressive Disorder?",
    "How should a social worker handle a dual relationship situation?",
    "What is the first step in developing a treatment plan for substance abuse?",
    "How do you maintain professional boundaries with clients?"
  ];

  return Array(count).fill(null).map((_, index) => ({
    question: {
      id: String(index + 1),
      category: categories[index % categories.length],
      question: questions[index % questions.length],
      correct_answer: "B",
      selected_answer: "A"
    },
    explanation: "I struggled with understanding the ethical implications...",
    date: new Date(Date.now() - index * 24 * 60 * 60 * 1000).toISOString(),
    isReviewed: index % 3 === 0,
    attempts: Math.floor(Math.random() * 3) + 1 // Random attempts between 1 and 3
  }));
};

// Create mock test results with wrong answers
const createMockTestResults = (): Record<string, TestResult> => {
  const mockWrongAnswers = generateMockWrongAnswers(15);
  const results = [
    {
      id: '1',
      date: "2024-03-01",
      score: 60,
      totalQuestions: 100,
      percentage: 60,
      timeTaken: 6000, // 100 questions * 60 seconds = realistic test time
      questions: formattedQuestions,
      questionIds: formattedQuestions.map(q => q.id.toString()),
      wrongAnswers: mockWrongAnswers.slice(0, 7),
      type: safeQuizType.SAMPLE,
      adaptiveMetrics: {
        startingDifficulty: 4,
        endingDifficulty: 5,
        averageResponseTime: 65,
        categoryPerformance: {
          'ETHICS': { correct: 18, total: 25, averageTime: 62, successRate: 0.72 },
          'DIAGNOSIS': { correct: 15, total: 25, averageTime: 68, successRate: 0.60 },
          'TREATMENT_PLANNING': { correct: 12, total: 25, averageTime: 70, successRate: 0.48 },
          'HUMAN_DEVELOPMENT': { correct: 15, total: 25, averageTime: 65, successRate: 0.60 }
        },
        overallSuccessRate: 0.60,
        totalQuestionsAttempted: 100,
        currentStreak: 3,
        longestStreak: 7,
        lastSessionDate: new Date().toISOString(),
        weeklyActivityStreak: 2,
        studyTimeThisWeek: 420,
        estimatedExamReadiness: 6,
        completedCategories: ['ETHICS'],
        mastery: {
          beginner: ['TREATMENT_PLANNING'],
          intermediate: ['DIAGNOSIS', 'HUMAN_DEVELOPMENT'],
          advanced: ['ETHICS']
        },
        recentPerformance: [
          { date: '2024-03-01', score: 60, category: 'ETHICS', questionsAttempted: 25 }
        ],
        preferredTimes: [
          { dayOfWeek: 'Monday', timeOfDay: 'morning', averagePerformance: 0.65 }
        ],
        difficultyProgression: [
          { date: '2024-03-01', difficulty: 4, successRate: 0.60 }
        ]
      }
    },
    {
      id: '2',
      date: "2024-03-15",
      score: 75,
      totalQuestions: 100,
      percentage: 75,
      timeTaken: 5500, // 100 questions * 55 seconds = improved pacing
      questions: formattedQuestions,
      questionIds: formattedQuestions.map(q => q.id.toString()),
      wrongAnswers: mockWrongAnswers.slice(7, 11),
      type: safeQuizType.SAMPLE,
      adaptiveMetrics: {
        startingDifficulty: 0,
        endingDifficulty: 0,
        averageResponseTime: 0,
        categoryPerformance: {},
        overallSuccessRate: 0,
        totalQuestionsAttempted: 0,
        currentStreak: 0,
        longestStreak: 0,
        lastSessionDate: new Date().toISOString(),
        weeklyActivityStreak: 0,
        studyTimeThisWeek: 0,
        estimatedExamReadiness: 0,
        completedCategories: [],
        mastery: {
          beginner: [],
          intermediate: [],
          advanced: []
        },
        recentPerformance: [],
        preferredTimes: [],
        difficultyProgression: []
      }
    },
    {
      id: '3',
      date: "2024-03-30",
      score: 82,
      totalQuestions: 100,
      percentage: 82,
      timeTaken: 4800, // 100 questions * 48 seconds = better pacing
      questions: formattedQuestions,
      questionIds: formattedQuestions.map(q => q.id.toString()),
      wrongAnswers: mockWrongAnswers.slice(11, 15),
      type: safeQuizType.SAMPLE,
      adaptiveMetrics: {
        startingDifficulty: 6,
        endingDifficulty: 7,
        averageResponseTime: 48,
        categoryPerformance: {
          'ETHICS': { correct: 23, total: 25, averageTime: 45, successRate: 0.92 },
          'DIAGNOSIS': { correct: 21, total: 25, averageTime: 50, successRate: 0.84 },
          'TREATMENT_PLANNING': { correct: 19, total: 25, averageTime: 52, successRate: 0.76 },
          'HUMAN_DEVELOPMENT': { correct: 19, total: 25, averageTime: 48, successRate: 0.76 }
        },
        overallSuccessRate: 0.82,
        totalQuestionsAttempted: 100,
        currentStreak: 8,
        longestStreak: 12,
        lastSessionDate: new Date().toISOString(),
        weeklyActivityStreak: 4,
        studyTimeThisWeek: 600,
        estimatedExamReadiness: 8,
        completedCategories: ['ETHICS', 'DIAGNOSIS'],
        mastery: {
          beginner: [],
          intermediate: ['TREATMENT_PLANNING', 'HUMAN_DEVELOPMENT'],
          advanced: ['ETHICS', 'DIAGNOSIS']
        },
        recentPerformance: [
          { date: '2024-03-30', score: 82, category: 'ETHICS', questionsAttempted: 25 }
        ],
        preferredTimes: [
          { dayOfWeek: 'Monday', timeOfDay: 'morning', averagePerformance: 0.85 }
        ],
        difficultyProgression: [
          { date: '2024-03-30', difficulty: 7, successRate: 0.82 }
        ]
      }
    }
  ];
  
  // Convert array to Record using id as key
  return results.reduce((acc, result) => {
    acc[result.id] = result as TestResult;
    return acc;
  }, {} as Record<string, TestResult>);
};

// Mock adaptive learning data
const mockAdaptiveLearning: AdaptiveMetrics = {
  startingDifficulty: 5,
  endingDifficulty: 7,
  averageResponseTime: 75,
  categoryPerformance: {
    'ETHICS': {
      correct: 45,
      total: 60,
      averageTime: 68,
      successRate: 0.75
    },
    'DIAGNOSIS': {
      correct: 30,
      total: 50,
      averageTime: 82,
      successRate: 0.60
    },
    'TREATMENT_PLANNING': {
      correct: 20,
      total: 40,
      averageTime: 76,
      successRate: 0.50
    }
  },
  overallSuccessRate: 0.68,
  totalQuestionsAttempted: 150,
  currentStreak: 4,
  longestStreak: 7,
  lastSessionDate: '2024-03-30',
  weeklyActivityStreak: 3,
  studyTimeThisWeek: 720,
  estimatedExamReadiness: 72,
  completedCategories: ['ETHICS'],
  mastery: {
    beginner: ['TREATMENT_PLANNING'],
    intermediate: ['DIAGNOSIS'],
    advanced: ['ETHICS']
  },
  recentPerformance: [
    {
      date: '2024-03-30',
      score: 82,
      category: 'ETHICS',
      questionsAttempted: 20
    },
    {
      date: '2024-03-29',
      score: 75,
      category: 'DIAGNOSIS',
      questionsAttempted: 15
    },
    {
      date: '2024-03-28',
      score: 68,
      category: 'TREATMENT_PLANNING',
      questionsAttempted: 18
    }
  ],
  preferredTimes: [
    {
      dayOfWeek: 'Monday',
      timeOfDay: 'evening',
      averagePerformance: 0.75
    },
    {
      dayOfWeek: 'Wednesday',
      timeOfDay: 'morning',
      averagePerformance: 0.82
    }
  ],
  difficultyProgression: [
    {
      date: '2024-03-28',
      difficulty: 5,
      successRate: 0.65
    },
    {
      date: '2024-03-29',
      difficulty: 6,
      successRate: 0.70
    },
    {
      date: '2024-03-30',
      difficulty: 7,
      successRate: 0.75
    }
  ]
};

// Mock data for AdaptiveLearningTypes interface (used by ProgressDashboard)
export const mockAdaptiveLearningMetrics: AdaptiveLearningMetrics = {
  startingDifficulty: 5,
  endingDifficulty: 6,
  averageResponseTime: 85,
  categoryPerformance: {},
  overallSuccessRate: 0.82,
  totalQuestionsAttempted: 450,
  currentStreak: 12,
  longestStreak: 18,
  lastSessionDate: new Date().toISOString(),
  weeklyActivityStreak: 3,
  studyTimeThisWeek: 172800,
  estimatedExamReadiness: 0.76,
  completedCategories: [],
  mastery: { beginner: [], intermediate: [], advanced: [] },
  recentPerformance: [],
  preferredTimes: [],
  difficultyProgression: []
};
// Create mock user using the User class with corrected properties
export const mockUser: User = {
    id: "mock-student-demo",
    email: "student.demo@example.com",
    profile: {
        name: "Alex Student",
        examDate: "2025-05-15",
        studyGoal: "Pass LCSW exam with confidence and master all categories",
        educationLevel: safeEducationLevel.MASTERS,
        lastUpdated: new Date().toISOString(),
        preferredStudyTime: safePreferredStudyTime.EVENING,
        challengingTopics: [
            safeChallengingTopic.DIAGNOSIS,
            safeChallengingTopic.TREATMENT_PLANNING
        ],
        learningStyle: safeLearningStyle.KINESTHETIC,
        onboardingCompleted: true
    },
    quizProgress: null,
    testResults: createMockTestResults(),
    subscriptionStatus: "active",
    isSubscribed: true,
    adaptiveLearning: mockAdaptiveLearning,
    wrongAnswers: generateMockWrongAnswers(25),
    quizzes: {},
    // Enhanced dashboard stats for active user
    totalQuestionsAttempted: 450,
    correctAnswers: 315,
    studyStreak: 12,
    masteryLevel: 75,
    averageScore: 78,
    scoreTrend: { direction: 'up', percentage: 8 },
    completedTests: 8,
    totalStudyHours: 48,
    studyHoursTrend: { direction: 'up', percentage: 15 },
    latestTestResult: {
        id: 'latest-test',
        date: new Date(Date.now() - 2 * 24 * 60 * 60 * 1000).toISOString(),
        score: 42, // Correct answers (85% of 50 = 42.5, rounded down)
        totalQuestions: 50,
        percentage: 85,
        timeTaken: 2400,
        questions: formattedQuestions.slice(0, 50),
        questionIds: formattedQuestions.slice(0, 50).map(q => q.id),
        wrongAnswers: generateMockWrongAnswers(8),
        type: safeQuizType.SAMPLE,
        adaptiveMetrics: mockAdaptiveLearning
    },
    totalWrongAnswers: 25,
    stripeCustomerId: "cus_demo_student",
    subscriptionId: "sub_demo_active"
};

// Export wrong answers separately if needed
export const mockWrongAnswers: WrongAnswer[] = Object.values(mockUser.testResults).reduce(
  (acc, test) => [...acc, ...test.wrongAnswers], 
  [] as WrongAnswer[]
);

// Export formatted questions for use in other components
export const availableQuestions = formattedQuestions;